import React, { useRef } from 'react';

import { useDragScroll } from 'components/SaloonLobby/useDragScroll';

import styles from './TypeRow.module.scss';

export type ChipId = string;

export interface ChipDef {
  id: ChipId;
  label: string;
  variant?: 'default' | 'dark';
  // Optional leading icon. SVG should use currentColor so it inherits the
  // chip's text color across default / hover / active states.
  icon?: React.ReactNode;
}

interface TypeRowProps {
  chips: ChipDef[];
  selected: Set<ChipId>;
  onToggle: (id: ChipId) => void;
  /**
   * Render at full width with no scroller of its own, for a parent that scrolls
   * the whole bar (the lobby's filter bar carries the pills AND the club stats in
   * one carousel — owner, 2026-09-08). Default: the row scrolls itself.
   */
  inline?: boolean;
  /**
   * A class for the row's root, so a parent that lays this row out among its
   * own children (the lobby's filter bar) names it with a class IT owns instead
   * of matching this module's generated ident (review, 2026-09-16).
   */
  className?: string;
}

const TypeRow: React.FC<TypeRowProps> = ({
  chips,
  selected,
  onToggle,
  inline = false,
  className
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // Inline, the parent bar is the scroller and owns the drag (SaloonLobby).
  useDragScroll(scrollRef, { enabled: !inline });

  return (
    <div
      ref={scrollRef}
      className={[styles.row, inline ? styles.rowInline : '', className ?? '']
        .filter(Boolean)
        .join(' ')}>
      {chips.map((chip) => {
        const isActive = selected.has(chip.id);
        const isDark = chip.variant === 'dark';
        const classes = [
          styles.chip,
          isDark ? styles.chipDark : styles.chipDefault,
          isActive ? (isDark ? styles.chipDarkActive : styles.chipActive) : ''
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            type='button'
            key={chip.id}
            className={classes}
            onClick={() => onToggle(chip.id)}
            aria-pressed={isActive}>
            {chip.icon && (
              <span className={styles.chipIcon} aria-hidden='true'>
                {chip.icon}
              </span>
            )}
            {chip.label}
          </button>
        );
      })}
    </div>
  );
};

export default TypeRow;
